import { GuideItem } from '../interface';
import {
  STUDENT_RE_ENROLLMENT_STATUS,
  TRANSITION_TYPE,
} from '../interface/reEnrollment-student.interface';
import { tr } from '../i18n/i18n';

// Re-enrollment cycle status enum
export enum ReEnrollmentCycleStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Status dropdown options — a function (not a static array) so each call
// re-reads tr() against the current language instead of freezing the
// translation at module-load time.
export const getStudentStatusOptions = () => [
  { label: tr('re_enrollment_options.all_status', 'All Status'), value: undefined },
  { label: tr('status.draft', 'Draft'), value: STUDENT_RE_ENROLLMENT_STATUS.DRAFT },
  { label: tr('status.pending', 'Pending'), value: STUDENT_RE_ENROLLMENT_STATUS.PENDING },
  { label: tr('status.promoted', 'Promoted'), value: STUDENT_RE_ENROLLMENT_STATUS.PROMOTED },
  { label: tr('status.demoted', 'Demoted'), value: STUDENT_RE_ENROLLMENT_STATUS.DEMOTED },
  { label: tr('status.failed', 'Failed'), value: STUDENT_RE_ENROLLMENT_STATUS.FAILED },
  { label: tr('ui.withdrawn', 'Withdrawn'), value: STUDENT_RE_ENROLLMENT_STATUS.WITHDRAWN },
  { label: tr('status.graduated', 'Graduated'), value: STUDENT_RE_ENROLLMENT_STATUS.GRADUATED },
  { label: tr('fe_labels.drop_off', 'Drop Off'), value: STUDENT_RE_ENROLLMENT_STATUS.DROP_OFF },
];

/**
 * Unified transition type dropdown options (UnifiedTransitionModal).
 * GRADUATE / DROP_OFF are terminal school-leaver transitions: no target
 * grade/classroom is selected and the student leaves their classroom.
 */
export const getTransitionTypeOptions = () => [
  { label: tr('re_enrollment_options.promote', 'Promote'), value: TRANSITION_TYPE.PROMOTE },
  { label: tr('re_enrollment_options.demote', 'Demote'), value: TRANSITION_TYPE.DEMOTE },
  { label: tr('re_enrollment_options.transfer', 'Transfer'), value: TRANSITION_TYPE.TRANSFER },
  { label: tr('re_enrollment_options.graduate', 'Graduate'), value: TRANSITION_TYPE.GRADUATE },
  { label: tr('fe_labels.drop_off', 'Drop Off'), value: TRANSITION_TYPE.DROP_OFF },
];

// Academic transition type enum
export enum ACADEMIC_TRANSITION_TYPE {
  INITIAL_ENROLLMENT = 'INITIAL_ENROLLMENT', // First admission
  PROMOTED = 'PROMOTED', // Regular promotion
  DEMOTED = 'DEMOTED', // Demotion
  FAILED = 'FAILED', // Failed/repeating grade
  WITHDRAWN = 'WITHDRAWN', // Left school
  GRADUATED = 'GRADUATED', // Completed school
  DROP_OFF = 'DROP_OFF', // Left school before completing the final grade
  TRANSFERRED = 'TRANSFERRED', // Transferred to another school
}

/**
 * Quick Guide & Rules content for re-enrollment screen
 * Displayed in collapsible section at top of page with primary color background
 */
export const getReEnrollmentGuideItems = (): GuideItem[] => [
  {
    title: tr('ui.academic_year_sync', 'Academic Year Sync'),
    description: tr(
      're_enrollment_options.guide_academic_year_sync_desc',
      'All re-enrollment actions automatically map strictly to your current Academic Year.',
    ),
    icon: 'pi pi-calendar',
  },
  {
    title: tr('ui.immediate_transfers', 'Immediate Transfers'),
    description: tr(
      're_enrollment_options.guide_immediate_transfers_desc',
      'Use for real-time, mid-year changes or emergency corrections. This instantly updates the active roster and parent apps.',
    ),
    icon: 'pi pi-arrow-right',
  },
  {
    title: tr('ui.not_yet_started', 'Not Yet Started'),
    description: tr(
      're_enrollment_options.guide_not_yet_started_desc',
      'Queue students (Promote, Demote, or Transfer) for the upcoming term while the cycle is still marked as Not Yet Started.',
    ),
    icon: 'pi pi-hourglass',
  },
  {
    title: tr('ui.active_status', 'Active Status'),
    description: tr(
      're_enrollment_options.guide_active_status_desc',
      'Transitioning the upcoming cycle to "Active" unlocks student movement, allowing the school to execute and finalize planning for the upcoming/planned year.',
    ),
    icon: 'pi pi-play',
  },
  {
    title: tr('ui.parent_consent', 'Parent Consent'),
    description: tr(
      're_enrollment_options.guide_parent_consent_desc',
      'If enabled, queued students stay Pending until parents approve via the app. If disabled, Admins can approve placements instantly without requiring parent action.',
    ),
    icon: 'pi pi-check',
  },
];
